import {
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
  Chip,
} from "@mui/material";
import React, { useEffect, useState, useRef, useCallback } from "react";
import DataTable from "react-data-table-component";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clientApis,
  clientCallRecords,
  clientDriversApis,
} from "../../../Data/axios";
import CallRecordingHeader from "./CallRecordingHeader";
import dayjs from "dayjs";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CallIcon from "@mui/icons-material/Call";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const tileStyles = {
  total: { bgcolor: "#e3f2fd", color: "#1565c0", icon: <CallIcon sx={{ fontSize: 28 }} /> },
  answered: { bgcolor: "#e8f5e9", color: "#2e7d32", icon: <CheckCircleIcon sx={{ fontSize: 28 }} /> },
  noanswer: { bgcolor: "#ffebee", color: "#c62828", icon: <CancelIcon sx={{ fontSize: 28 }} /> },
};

const statusColorMap = {
  ANSWER: { color: "#fff", bgcolor: "#4caf50" },
  "CANCEL-AGENT": { color: "#fff", bgcolor: "#ff9800" },
  "CANCEL-CUSTOMER": { color: "#fff", bgcolor: "#ff5722" },
  "NOT CONNECTED": { color: "#fff", bgcolor: "#9e9e9e" },
  "NON-ANSWERED": { color: "#fff", bgcolor: "#f44336" },
};

const stripPhonePrefix = (phone) => {
  if (!phone) return "";
  return phone.replace(/^\+?91/, "");
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "--";
  const mins = Math.floor(Number(seconds) / 60);
  const secs = Number(seconds) % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const ExpandableRow = ({ data }) => {
  const details = [
    { label: "Call Date", value: data.call_date },
    { label: "Start Time", value: data.startdatetime },
    { label: "End Time", value: data.enddatetime },
    { label: "Total Duration", value: formatDuration(data.total_duration) },
    { label: "Talk Duration", value: formatDuration(data.talk_duration) },
    { label: "Ringing Duration", value: formatDuration(data.ringing_duration) },
    { label: "Dialer Picked Time", value: data.LegA_Picked_time || "--" },
    { label: "Receiver Start Time", value: data.LegB_Start_time || "--" },
    { label: "Receiver Picked Time", value: data.LegB_Picked_time || "--" },
  ];

  return (
    <Box sx={{ p: 2, bgcolor: "#f9f9f9", borderBottom: "1px solid #eee" }}>
      <Grid container spacing={2}>
        {details.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.label}>
            <Typography variant="caption" sx={{ color: "#888", fontWeight: 600 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ color: "#333", mt: 0.25 }}>
              {item.value || "--"}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const CallRecordingClients = ({ clientId: clientIdProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientId = clientIdProp || location.state;

  const [startDate, setStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [callRecords, setCallRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailValue, setDetailValue] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage] = useState(25);

  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driversLoading, setDriversLoading] = useState(false);

  const [callStatus, setCallStatus] = useState("");

  const [answeredTotal, setAnsweredTotal] = useState(0);
  const [noAnswerTotal, setNoAnswerTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, [navigate]);

  useEffect(() => {
    getClientData();
    fetchActiveDrivers();
  }, [clientId]);

  const getClientData = async () => {
    try {
      setLoading(true);
      const rowData = await clientApis.clientViewMoreId(clientId);
      setDetailValue(rowData);
    } catch (err) {
      console.error("Error fetching client data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveDrivers = async () => {
    try {
      setDriversLoading(true);
      const result = await clientDriversApis.getClientDriversList(clientId);
      if (result?.success && result?.data?.active_drivers) {
        setDrivers(result.data.active_drivers);
      } else {
        setDrivers([]);
      }
    } catch (err) {
      console.error("Error fetching active drivers:", err);
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  };

  const { client } = detailValue || {};
  const { user_id } = client || {};

  useEffect(() => {
    if (user_id && startDate && endDate) {
      fetchCallRecords();
    }
  }, [user_id, startDate, endDate, selectedDriver, callStatus, currentPage]);

  const fetchCallRecords = async () => {
    try {
      setLoading(true);
      const filters = {
        start_date: startDate,
        end_date: endDate,
        per_page: perPage,
        current_page: currentPage,
      };

      if (selectedDriver?.phone) {
        filters.caller_num = stripPhonePrefix(selectedDriver.phone);
      }

      if (callStatus) {
        filters.callstatus = callStatus;
      }

      const result = await clientCallRecords.getClientDriverCallRecordings(
        clientId,
        filters
      );

      if (result?.success && result?.data) {
        const data = result.data;
        const records = Array.isArray(data.result) ? data.result : [];
        setCallRecords(records);
        setTotalRecords(data.total || records.length || 0);
        setAnsweredTotal(Number(data.answered_total) || 0);
        setNoAnswerTotal(Number(data.noanswer_total) || 0);
        setGrandTotal(Number(data.total) || records.length || 0);
      } else {
        setCallRecords([]);
        setTotalRecords(0);
        setAnsweredTotal(0);
        setNoAnswerTotal(0);
        setGrandTotal(0);
      }
    } catch (err) {
      console.error("Error fetching call records:", err);
      setCallRecords([]);
      setTotalRecords(0);
      setAnsweredTotal(0);
      setNoAnswerTotal(0);
      setGrandTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAudioPlay = useCallback(
    (record) => {
      if (!audioRef.current) return;

      if (playingId === record.id) {
        audioRef.current.pause();
        setPlayingId(null);
      } else {
        if (playingId !== null) {
          audioRef.current.pause();
        }
        audioRef.current.src = record.file;
        audioRef.current.play().catch(() => {});
        setPlayingId(record.id);
      }
    },
    [playingId]
  );

  const handleRowClicked = useCallback(
    (row) => {
      handleAudioPlay(row);
    },
    [handleAudioPlay]
  );

  const toggleExpand = useCallback((rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  }, []);

  const columns = [
    {
      name: "Driver",
      selector: (row) => row.caller_name || row.caller_num || "--",
      sortable: true,
      wrap: true,
      cell: (row) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
            {row.caller_name || "--"}
          </Typography>
          <Typography variant="caption" sx={{ color: "#888" }}>
            {row.caller_num || "--"}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Client",
      selector: (row) => row.member_name || row.member_num || "--",
      sortable: true,
      wrap: true,
      cell: (row) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
            {row.member_name || "--"}
          </Typography>
          <Typography variant="caption" sx={{ color: "#888" }}>
            {row.member_num || "--"}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Call Date",
      selector: (row) => row.call_date,
      sortable: true,
      cell: (row) => (
        <Typography variant="body2" sx={{ color: "#555" }}>
          {row.call_date || "--"}
        </Typography>
      ),
    },
    {
      name: "Duration",
      selector: (row) => Number(row.total_duration) || 0,
      sortable: true,
      cell: (row) => (
        <Typography variant="body2" sx={{ color: "#555", fontFamily: "monospace" }}>
          {formatDuration(row.total_duration)}
        </Typography>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.callstatus,
      sortable: true,
      cell: (row) => {
        const status = row.callstatus;
        const style = statusColorMap[status] || { color: "#333", bgcolor: "#f5f5f5" };
        return (
          <Chip
            label={row.callstatus || "--"}
            size="small"
            sx={{
              color: style.color,
              bgcolor: style.bgcolor,
              fontWeight: 600,
              fontSize: "11px",
              height: "24px",
            }}
          />
        );
      },
    },
    {
      name: "Recording",
      cell: (row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleAudioPlay(row);
          }}
          sx={{
            bgcolor: playingId === row.id ? "#635ee7" : "#f0f0f0",
            color: playingId === row.id ? "#fff" : "#635ee7",
            width: 36,
            height: 36,
            "&:hover": {
              bgcolor: playingId === row.id ? "#5048c9" : "#e0e0e0",
            },
          }}
        >
          {playingId === row.id ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
      ),
      ignoreRowClick: true,
      width: "80px",
    },
    {
      name: "",
      cell: (row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand(row.id);
          }}
          sx={{ color: "#888" }}
        >
          {expandedRows[row.id] ? (
            <KeyboardArrowUpIcon />
          ) : (
            <KeyboardArrowDownIcon />
          )}
        </IconButton>
      ),
      ignoreRowClick: true,
      width: "50px",
    },
  ];

  const expandableRowComponent = useCallback(
    (props) => <ExpandableRow {...props} />,
    []
  );

  const conditionalRowStyles = [
    {
      when: (row) => playingId === row.id,
      style: { backgroundColor: "#f5f3ff" },
    },
  ];

  return (
    <Box>
      <audio ref={audioRef} preload="none" />

      <CallRecordingHeader
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedDriver={selectedDriver}
        setSelectedDriver={setSelectedDriver}
        drivers={drivers}
        driversLoading={driversLoading}
        callStatus={callStatus}
        setCallStatus={setCallStatus}
      />

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: "Total Calls", value: grandTotal, key: "total" },
            { label: "Total Answered", value: answeredTotal, key: "answered" },
            { label: "Total Non-Answered", value: noAnswerTotal, key: "noanswer" },
          ].map((tile) => (
            <Grid item xs={12} sm={4} key={tile.key}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                  bgcolor: tileStyles[tile.key].bgcolor,
                }}
              >
                <Box sx={{ color: tileStyles[tile.key].color }}>
                  {tileStyles[tile.key].icon}
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: tileStyles[tile.key].color }}
                  >
                    {tile.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: tileStyles[tile.key].color, opacity: 0.8 }}>
                    {tile.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <DataTable
          columns={columns}
          data={callRecords}
          progressPending={loading}
          pagination
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 100]}
          highlightOnHover
          pointerOnHover
          onRowClicked={handleRowClicked}
          expandableRows
          expandableRowsComponent={expandableRowComponent}
          expandableRowExpanded={(row) => !!expandedRows[row.id]}
          conditionalRowStyles={conditionalRowStyles}
          noDataComponent={
            <Box sx={{ py: 4 }}>
              <Typography sx={{ color: "#888" }}>
                {loading ? "Loading..." : "No call records found"}
              </Typography>
            </Box>
          }
          customStyles={{
            headCells: {
              style: {
                backgroundColor: "#f2f2f2",
                fontWeight: 600,
                fontSize: "13px",
                color: "#555",
                borderBottom: "1px solid #ddd",
              },
            },
            cells: {
              style: {
                fontSize: "13px",
                color: "#333",
                borderBottom: "1px solid #f0f0f0",
              },
            },
            rows: {
              style: {
                minHeight: "52px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#fafafa",
                },
              },
            },
            pagination: {
              style: {
                borderTop: "1px solid #eee",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default CallRecordingClients;
